/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import useRouting from "hooks/useRouting";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useAuth } from "state/AuthContext";
import { useGlobal } from "state/GeneralContext";
import { Button, FontAwesomeIcon } from "..";
import Logo from "./Logo";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "components/LanguageSwitcher";
import FEATURE_FLAGS from "state/featureFlags/flags";
import FeatureFlagController from "state/featureFlags/FeatureFlagController";
import WebpagesNav from "./WebpagesNav";

import { pushToDataLayer } from "utils/gtm";

const Header = () => {
  const { t } = useTranslation();
  const { handleLogout } = useAuth();
  const { hasNotifications, isLoggedIn, events } = useGlobal();
  const navigate = useNavigate();
  const { logoRoute } = useRouting();
  const { isCurrentPage, eventId } = useEventRouting();
  const isInExitPage = isCurrentPage(ROUTES.onboard.exit);
  const isSignUpPage = isCurrentPage(ROUTES.registerLink.signup);
  const isUnsubscribePage = isCurrentPage(ROUTES.unsubscribe);
  const showEventSwitcher =
    events.length > 1 && isLoggedIn && !isCurrentPage(ROUTES.eventSwitcher);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header if scrolling up or at the top
      // Hide header if scrolling down and past a threshold (e.g., 100px)
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const currentEvent = events.find((e) => String(e.id) === String(eventId));
  const eventName = currentEvent?.title || "";

  const handleMobileMenu = (isOpen: boolean) => {
    setIsMobileMenuOpen(isOpen);
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  };

  // Pages that should hide specific elements
  const finalShowNotification =
    !isUnsubscribePage &&
    !isInExitPage &&
    !isSignUpPage &&
    isLoggedIn &&
    eventId;

  if (isCurrentPage(ROUTES.noAccess)) return null;

  return (
    <>
      <div
        className={classNames(
          "flex justify-between items-center",
          "p-3 ps-8 -rounded-outer-container mx-4 md:mx-20 -bg--theme-colors-surface-neutral-level04 z-[60]",
          "sticky inset-x-0 top-4 md:top-8 mb-4 md:mb-8 transition-all duration-700 ease-in-out",
          {
            "-translate-y-[151%] opacity-0": !isVisible,
            "translate-y-0 opacity-100": isVisible,
          },
        )}
      >
        {/* Left section: Logo and Language Picker */}
        <div className="flex items-center gap-x-4">
          <Button
            as="link"
            variant="link"
            to={logoRoute}
            className={classNames("ps-0 min-h-[auto]", {
              "pointer-events-none": !eventId,
            })}
            onClick={() => handleMobileMenu(false)}
          >
            <Logo />
          </Button>
          <FeatureFlagController flag={FEATURE_FLAGS.localization}>
            <LanguageSwitcher className="min-h-[auto] " />
          </FeatureFlagController>
        </div>

        <WebpagesNav
          isLoggedIn={isLoggedIn}
          handleMobileMenu={handleMobileMenu}
        />

        {/* Right actions */}
        <div className="flex items-center gap-x-2">
          {/* Desktop actions */}
          {showEventSwitcher && (
            <Button
              variant={"tertiary"}
              size="md"
              className="hidden min-h-[auto] lg:flex items-center gap-2   !px-3 !py-2"
              onClick={() => {
                pushToDataLayer({
                  event: "change_event",
                  screen_name: "attendance",
                  journey_step: "event selection",
                });
                navigate(ROUTES.eventSwitcher, { replace: true });
              }}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold max-w-[200px] truncate">
                  {eventName || t("switchEvents", "Switch Event")}
                </span>
                <span className="size-s20  flex items-center justify-center">
                  <FontAwesomeIcon name="fa-rotate-reverse" />
                </span>
              </div>
            </Button>
          )}

          {finalShowNotification && (
            <Button
              variant={"tertiary"}
              size="md"
              to={ROUTES.dashboard.notifications}
              className="hidden lg:block !p-2 min-h-[auto]   relative"
              as="link"
            >
              <div>
                {hasNotifications && (
                  <div className="h-2 w-2 rounded-full -bg--theme-colors-danger-default absolute top-1.5 right-1.5 z-[1]" />
                )}
                <div className="size-s20  flex items-center justify-center">
                  <FontAwesomeIcon name="fa-bell" size="sm" />
                </div>
              </div>
            </Button>
          )}

          {isLoggedIn ? (
            <Button
              variant={"tertiary"}
              size="md"
              className="hidden lg:block !p-2 min-h-[auto]  "
              onClick={() => {
                pushToDataLayer({
                  event: "logout",
                  screen_name: "header",
                  journey_step: "logout",
                });
                handleLogout();
              }}
            >
              <div className="size-s20 flex items-center justify-center ">
                <FontAwesomeIcon name="fa-sign-out-alt" size="sm" />
              </div>
            </Button>
          ) : (
            <Button
              variant={"tertiary"}
              size="md"
              className="hidden min-h-[auto] lg:flex items-center   !px-4"
              to={ROUTES.login}
              as="link"
            >
              <span className="text-sm font-bold">
                {t("header.signInLabel")}
              </span>
            </Button>
          )}

          {/* Mobile menu trigger */}
          <div className="lg:hidden flex items-center">
            <Button
              variant="tertiary"
              size="md"
              className="min-h-[auto] !p-2"
              onClick={() => handleMobileMenu(!isMobileMenuOpen)}
            >
              <div className="size-s20 flex items-center justify-center ">
                <FontAwesomeIcon name="fa-ellipsis-v" size="sm" />
              </div>
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Modal */}
        {isMobileMenuOpen && (
          <WebpagesNav.Mobile
            isLoggedIn={isLoggedIn}
            handleMobileMenu={handleMobileMenu}
            handleLogout={handleLogout}
          />
        )}
      </div>
    </>
  );
};

export default Header;
